/**
 * Splits text copied from Excel into rows and cells. Excel puts one line per
 * row and a tab between cells, and ends the copy with a line break: a single
 * trailing empty line is dropped, any other empty line is kept as a row.
 */
export function parsePastedGrid(text: string): string[][] {
  const lines = text.split(/\r\n|\r|\n/);
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines.map((line) => line.split('\t'));
}

/**
 * Width of the widest row. Excel copies a rectangular range, empty cells
 * included, so a block copied one column too wide is caught even when that
 * column is empty.
 */
export function getGridColumnCount(rows: string[][]): number {
  return rows.reduce((max, row) => Math.max(max, row.length), 0);
}

export function getCell(rows: string[][], line: number, column: number) {
  return (rows[line - 1]?.[column] ?? '').trim();
}

export type ParsedAmount =
  | { kind: 'empty' }
  | { kind: 'amount'; value: number }
  | { kind: 'notAnAmount' }
  | { kind: 'invalid' };

/**
 * Reads an amount as Excel copies it in French: "206", "267,41",
 * "1 229,00" (regular, no-break or narrow no-break spaces), "62.1".
 */
export function parsePastedAmount(raw: string): ParsedAmount {
  const value = raw.replace(/[\s  ]/g, '');
  if (value === '') return { kind: 'empty' };

  const match = /^(-?)(\d+)(?:[.,](\d+))?$/.exec(value);
  if (!match) return { kind: 'notAnAmount' };

  const [, sign, integerPart, decimals = ''] = match;
  if (sign === '-' || decimals.length > 2) return { kind: 'invalid' };

  return {
    kind: 'amount',
    value: Number(decimals ? `${integerPart}.${decimals}` : integerPart),
  };
}

/**
 * Reads a dd/mm/yy or dd/mm/yyyy date (also accepts "." or "-" separators).
 * Returns a local-midnight Date, like the date pickers, or null when the text
 * is not a real calendar date (31/02/27 is rejected).
 */
export function parsePastedDate(raw: string): Date | null {
  const match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(raw.trim());
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year =
    match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}
